import React from 'react';
import { RankingValue } from 'components/common/RankingValue/RankingValue';
import { ScoreCell } from 'components/common/ScoreCell/ScoreCell';

import { CandidatesWithPosition } from '@types';
import { ICandidatesViewState } from '@hooks';
import { CandidateDetailsAction } from '@constants';
import { CandidateColumns, ICandidateColumn } from '../columns';
import { CandidateLookups, GroupedCandidates } from '../grouping';
import { CandidateListAction } from './CandidateListAction';
import { CandidateMaterials } from './CandidateMaterials';
import { CandidateNameCell } from './CandidateNameCell';
import { GroupedTable, useResolvedColumns } from './GroupedTable';
import { MetaCell } from './MetaCell';
import { StackCell } from './StackCell';

interface ICandidatesTableProps {
  groups: GroupedCandidates[];
  lookups: CandidateLookups;
  view: ICandidatesViewState;
  positionNameOf: (candidateId: string) => string | undefined;
  onSort: (key: string) => void;
  onCandidateClick: (id: string) => void;
  onCandidateAction: (id: string, action: CandidateDetailsAction) => void;
}

export const CandidatesTable: React.FC<ICandidatesTableProps> = ({
  groups,
  lookups,
  view,
  positionNameOf,
  onSort,
  onCandidateClick,
  onCandidateAction,
}) => {
  const { columns, isLaptop } = useResolvedColumns(
    CandidateColumns,
    view.columns,
  );

  const { positionTitlesById, workstreamsById } = lookups;

  const hasAnyVideo = groups.some((group) =>
    group.candidates.some((candidate) => Boolean(candidate.videoFileName)),
  );

  const renderCell = (
    column: ICandidateColumn,
    candidate: CandidatesWithPosition,
  ) => {
    switch (column.id) {
      case 'candidate':
        return (
          <CandidateNameCell
            candidate={candidate}
            positionName={isLaptop ? undefined : positionNameOf(candidate.id)}
          />
        );
      case 'workstream':
        return <MetaCell values={workstreamsById.get(candidate.id)} />;
      case 'position':
        return <MetaCell values={positionTitlesById.get(candidate.id)} />;
      case 'tt':
        return <ScoreCell testTask={candidate.testTask} label="test task" />;
      case 'technicalRanking':
        return (
          <RankingValue value={candidate.ranking?.technicalInterview ?? null} />
        );
      case 'softSkills':
        return <RankingValue value={candidate.ranking?.softSkills ?? null} />;
      case 'materials':
        return (
          <CandidateMaterials
            candidate={candidate}
            showVideo={hasAnyVideo}
            onPlay={() => onCandidateClick(candidate.id)}
          />
        );
      case 'stack':
        return <StackCell skills={candidate.skills} />;
      case 'nextSteps':
        return (
          <CandidateListAction
            candidate={candidate}
            onAction={onCandidateAction}
          />
        );
      default:
        return null;
    }
  };

  return (
    <GroupedTable
      groups={groups}
      columns={columns}
      sort={view.sort}
      onSort={onSort}
      onRowClick={onCandidateClick}
      renderCell={renderCell}
      isHighlighted={(candidate) => Boolean(candidate.isStarred)}
    />
  );
};
